import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  BuyContract,
  AutoBuyConfig,
  CreateBuyContractRequest,
  UpdateAutoBuyConfigRequest,
  UpdateBuyContractRequest,
  GetBuyContractsRequest,
  ListBuyContractsResult,
  BuyContractAllocationsResult,
  PublicBuyContractResponse,
  GetAdminBuyContractsRequest,
  GetAdminBuyContractsResult,
  GetBuyContractStatsRequest,
  BuyContractStatsReport,
  StockLeasesResult,
  ArchiveInactiveBuyContractsRequest,
  ArchiveInactiveBuyContractsResult,
  CardHistory,
  ListTupleReportsResponse,
} from './buy-contract.type';
import { UpdateContractBillingRequest } from '../player-auction.type';

@Injectable({ providedIn: 'root' })
export class BuyContractService {
  private contractUrl = environment.apiBaseURL + '/player-auction/buy-contract';
  private publicContractUrl = environment.apiBaseURL + '/player-auction/public/buy-contract';
  private autoBuyUrl = environment.apiBaseURL + '/player-auction/admin/auto-buy';
  private adminContractUrl = environment.apiBaseURL + '/player-auction/admin/buy-contract';

  constructor(private http: HttpClient) { }

  getBuyContracts(request: GetBuyContractsRequest = {}): Observable<ListBuyContractsResult> {
    let params = new HttpParams();
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex.toString());
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize.toString());
    if (request.status) params = params.set('status', request.status);
    if (request.search) params = params.set('search', request.search);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: ListBuyContractsResult }>(this.contractUrl, { params })
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/buy-contract/stats-report — aggregate stats for the caller's visible contracts matching the given filters */
  getBuyContractStatsReport(request: GetBuyContractStatsRequest = {}): Observable<BuyContractStatsReport> {
    let params = new HttpParams();
    if (request.search) params = params.set('search', request.search);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: BuyContractStatsReport }>(`${this.contractUrl}/stats-report`, { params })
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/buy-contract/open-counts — caller-visible Active buy contracts, for the page tabs. Reads no cards, so it stays fast. */
  getBuyContractOpenCounts(): Observable<Partial<Record<string, number>>> {
    return this.http
      .get<{ result: { status_counts: Partial<Record<string, number>> } }>(`${this.contractUrl}/open-counts`)
      .pipe(map(r => r.result.status_counts));
  }

  getBuyContract(id: string): Observable<BuyContract> {
    return this.http
      .get<{ result: BuyContract }>(`${this.contractUrl}/${id}`)
      .pipe(map(r => r.result));
  }

  createBuyContract(request: CreateBuyContractRequest): Observable<BuyContract> {
    return this.http
      .post<{ result: BuyContract }>(this.contractUrl, request)
      .pipe(map(r => r.result));
  }

  getBuyContractAllocations(id: string): Observable<BuyContractAllocationsResult> {
    return this.http
      .get<{ result: BuyContractAllocationsResult }>(`${this.contractUrl}/${id}/allocations`)
      .pipe(map(r => r.result));
  }

  patchBuyContract(id: string, req: UpdateBuyContractRequest): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.contractUrl}/${id}`, req)
      .pipe(map(r => r.result));
  }

  cancelBuyContract(id: string): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.contractUrl}/${id}/cancel`, {})
      .pipe(map(r => r.result));
  }

  resumeBuyContract(id: string): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.contractUrl}/${id}/resume`, {})
      .pipe(map(r => r.result));
  }

  releaseBuyContractLocks(id: string): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.contractUrl}/${id}/release-locks`, {})
      .pipe(map(r => r.result));
  }

  archiveBuyContract(id: string, archived: boolean): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.contractUrl}/${id}/archive`, { archived })
      .pipe(map(r => r.result));
  }

  /** No bulk archive endpoint exists for buy contracts — fan out per id. */
  archiveBuyContracts(ids: string[], archived: boolean): Observable<BuyContract[]> {
    if (!ids.length) return of([]);
    return forkJoin(ids.map(id => this.archiveBuyContract(id, archived)));
  }

  /**
   * PATCH .../archive-inactive — archives every non-archived, non-Active contract in one
   * server-side statement. Search/date (and ownerRole in admin scope) narrow the sweep.
   */
  archiveInactiveBuyContracts(request: ArchiveInactiveBuyContractsRequest = {}, admin = false): Observable<ArchiveInactiveBuyContractsResult> {
    let params = new HttpParams();
    if (request.search) params = params.set('search', request.search);
    if (request.ownerRole) params = params.set('ownerRole', request.ownerRole);
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    const url = admin ? this.adminContractUrl : this.contractUrl;
    return this.http
      .patch<{ result: ArchiveInactiveBuyContractsResult }>(`${url}/archive-inactive`, null, { params })
      .pipe(map(r => r.result));
  }

  getPublicBuyContract(id: string): Observable<PublicBuyContractResponse> {
    return this.http
      .get<{ result: PublicBuyContractResponse }>(`${this.publicContractUrl}/${id}`)
      .pipe(map(r => r.result));
  }

  getPublicBuyContractAllocations(id: string): Observable<BuyContractAllocationsResult> {
    return this.http
      .get<{ result: BuyContractAllocationsResult }>(`${this.publicContractUrl}/${id}/allocations`)
      .pipe(map(r => r.result));
  }

  // Admin billing management
  adminGetBuyContracts(request: GetAdminBuyContractsRequest = {}): Observable<GetAdminBuyContractsResult> {
    let params = new HttpParams();
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex.toString());
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize.toString());
    if (request.status) params = params.set('status', request.status);
    if (request.search) params = params.set('search', request.search);
    if (request.ownerRole) params = params.set('ownerRole', request.ownerRole);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: GetAdminBuyContractsResult }>(this.adminContractUrl, { params })
      .pipe(map(r => r.result));
  }

  updateBuyContractBilling(id: string, req: UpdateContractBillingRequest): Observable<BuyContract> {
    return this.http
      .patch<{ result: BuyContract }>(`${this.adminContractUrl}/${id}/billing`, req)
      .pipe(map(r => r.result));
  }

  // Continuous auto-buy (admin-only): the stock fleet is operational plumbing.

  getAutoBuyConfig(): Observable<AutoBuyConfig> {
    return this.http
      .get<{ result: AutoBuyConfig }>(this.autoBuyUrl)
      .pipe(map(r => r.result));
  }

  updateAutoBuyConfig(req: UpdateAutoBuyConfigRequest): Observable<AutoBuyConfig> {
    return this.http
      .patch<{ result: AutoBuyConfig }>(this.autoBuyUrl, req)
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/admin/buy-contract/:id/leases — drain runs, newest first. */
  getStockLeases(id: string): Observable<StockLeasesResult> {
    return this.http
      .get<{ result: StockLeasesResult }>(`${this.adminContractUrl}/${id}/leases`)
      .pipe(map(r => r.result));
  }

  /**
   * GET .../leases/:leaseId/screenshot?type=start|end — raw image, not JSON.
   * The stock account's credit before (start) and after (end) the drain run.
   */
  getStockLeaseScreenshot(id: string, leaseId: string, type: 'start' | 'end' = 'start'): Observable<Blob> {
    return this.http.get(`${this.adminContractUrl}/${id}/leases/${leaseId}/screenshot?type=${type}`, {
      responseType: 'blob',
    });
  }

  /** GET /player-auction/admin/buy-contract/tuple-reports — admin only, newest first. */
  listTupleReports(pageIndex: number, pageSize: number, reporterId?: string): Observable<ListTupleReportsResponse> {
    const params: Record<string, string | number> = { pageIndex, pageSize };
    if (reporterId) params['reporterId'] = reporterId;
    return this.http
      .get<{ result: ListTupleReportsResponse }>(`${this.adminContractUrl}/tuple-reports`, { params })
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/admin/buy-contract/card/:cardId/history — admin only. */
  getCardHistory(cardId: string): Observable<CardHistory> {
    return this.http
      .get<{ result: CardHistory }>(`${this.adminContractUrl}/card/${cardId}/history`)
      .pipe(map(r => r.result));
  }
}
